import { useEffect, useMemo, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  CheckCircle2,
  Cloud,
  ExternalLink,
  Gauge,
  Globe,
  Minus,
  RefreshCw,
  Search,
  ShieldCheck,
  Webhook,
} from 'lucide-react';
import { useI18n, useTranslation } from '@solara/i18n';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  ProviderBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
  useToast,
} from '@solara/ui-shared';
import type {
  TickstarApiEndpoint,
  TickstarDlqEntry,
  TickstarSlaCard,
  TickstarUsageSnapshot,
  TickstarWebhookEvent,
} from '@solara/sample-data';
import { usePeppolGateway, useTransaction } from '@solara/peppol-tickstar/react';
import { PeppolProviderError, type Transaction } from '@solara/peppol-provider';
import { useWebhooks, useReplayWebhook } from '@solara/api-client/react';
import { useAuth } from '../auth/AuthContext';
import {
  MOCK_TICKSTAR_API,
  MOCK_TICKSTAR_DLQ,
  MOCK_TICKSTAR_SLA,
  MOCK_TICKSTAR_USAGE,
  MOCK_TICKSTAR_WEBHOOKS,
} from '../data/mockData';

/**
 * Admin · TickStar integration dashboard.
 *
 * Shows the Solara White-label bundle status against the TickStar Peppol
 * service: API matrix, webhook delivery log, dead-letter queue, monthly
 * usage vs. Starter-tier quotas, and SLA / service-credit calculator.
 *
 * Connectivity defaults to the in-memory mock provider from
 * `@solara/peppol-tickstar/mock` until live TickStar credentials are wired
 * up — surfaced via the `ProviderBadge` at the top of the page.
 */
export default function TickstarPage() {
  const { t } = useTranslation('tickstar');
  const { t: tCommon } = useTranslation('common');
  const { t: tP1 } = useTranslation('p1');
  const { locale } = useI18n();
  const { can } = useAuth();
  const { toast } = useToast();
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }),
    [locale],
  );
  const numberFmt = useMemo(() => new Intl.NumberFormat(locale), [locale]);
  const pctFmt = useMemo(
    () => new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 2 }),
    [locale],
  );
  const eurFmt = useMemo(
    () => new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }),
    [locale],
  );

  // Connectivity surfaced from the shared PeppolGatewayProvider — same bundle
  // the customer portal uses, so transactions created from the portal show up
  // here live in the "demo" tab.
  const gateway = usePeppolGateway();
  const provider = gateway.connectivity;

  const [endpoints] = useState<TickstarApiEndpoint[]>(MOCK_TICKSTAR_API);
  const [webhooks, setWebhooks] = useState<TickstarWebhookEvent[]>(MOCK_TICKSTAR_WEBHOOKS);
  const [dlq, setDlq] = useState<TickstarDlqEntry[]>(MOCK_TICKSTAR_DLQ);
  const slaCards: TickstarSlaCard[] = MOCK_TICKSTAR_SLA;
  const usage: TickstarUsageSnapshot = MOCK_TICKSTAR_USAGE;
  // Backend DLQ stream — replay calls the real webhook endpoint.
  const backendDlqQuery = useWebhooks({ delivery: 'dlq' });
  const replayWebhook = useReplayWebhook();
  void backendDlqQuery;

  const [search, setSearch] = useState('');

  const filteredWebhooks = useMemo(() => {
    if (!search) return webhooks;
    const q = search.toLowerCase();
    return webhooks.filter(
      (w) =>
        w.topic.toLowerCase().includes(q) ||
        w.target.toLowerCase().includes(q) ||
        (w.transactionId?.toLowerCase().includes(q) ?? false) ||
        (w.errorMessage?.toLowerCase().includes(q) ?? false),
    );
  }, [webhooks, search]);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{t('admin.page.title')}</h1>
          <p className="text-sm text-slate-600">{t('admin.page.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          {provider.mode === 'mock' ? (
            <ProviderBadge
              variant="live"
              label={t('admin.overview.modeMock')}
              sub={provider.label}
              className="bg-amber-50 text-amber-800 border-amber-200"
            />
          ) : (
            <ProviderBadge variant="live" label={t('admin.overview.modeLive')} sub={provider.label} />
          )}
          <Button
            variant="ghost"
            onClick={() =>
              toast({ title: 'Refreshed', description: 'Fixture re-seeded from sample-data.' })
            }
          >
            <RefreshCw className="me-1 h-4 w-4" />
            {tCommon('actions.refresh', { defaultValue: 'Refresh' })}
          </Button>
        </div>
      </header>

      {provider.mode === 'mock' ? (
        <ProviderBadge
          variant="demo"
          label={t('admin.overview.modeMock')}
          sub={t('admin.overview.demoBanner')}
        />
      ) : null}

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">{t('admin.tabs.overview')}</TabsTrigger>
          <TabsTrigger value="apiMatrix">{t('admin.tabs.apiMatrix')}</TabsTrigger>
          <TabsTrigger value="webhooks">{t('admin.tabs.webhooks')}</TabsTrigger>
          <TabsTrigger value="dlq">{t('admin.tabs.dlq')}</TabsTrigger>
          <TabsTrigger value="sla">{t('admin.tabs.sla')}</TabsTrigger>
          <TabsTrigger value="usage">{t('admin.tabs.usage')}</TabsTrigger>
          <TabsTrigger value="demo">{tP1('tickstar.liveDemo')}</TabsTrigger>
          <TabsTrigger value="readiness">{tP1('tickstar.prodReadiness')}</TabsTrigger>
        </TabsList>

        {/* OVERVIEW ------------------------------------------------ */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
            <OverviewCard
              icon={<ShieldCheck className="h-5 w-5 text-emerald-600" />}
              label={t('admin.overview.bundleLabel')}
              value={usage.bundle}
            />
            <OverviewCard
              icon={<Gauge className="h-5 w-5 text-blue-600" />}
              label={t('admin.overview.tierLabel')}
              value={usage.tier}
            />
            <OverviewCard
              icon={<Globe className="h-5 w-5 text-violet-600" />}
              label={t('admin.overview.regionLabel')}
              value={usage.hostingRegion}
            />
            <OverviewCard
              icon={<Cloud className="h-5 w-5 text-amber-600" />}
              label={t('admin.overview.modeLabel')}
              value={provider.label}
            />
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <UsageCard
              label={t('admin.usage.transactions')}
              used={usage.monthlyTransactionsUsed}
              cap={usage.monthlyTransactionCap}
              fmt={numberFmt}
            />
            <UsageCard
              label={t('admin.usage.participants')}
              used={usage.participantsRegistered}
              cap={usage.participantCap}
              fmt={numberFmt}
            />
            <UsageCard
              label={t('admin.usage.lookups')}
              used={usage.lookupsUsedThisMonth}
              cap={usage.lookupCap}
              fmt={numberFmt}
            />
          </div>

          <Card className="mt-6">
            <CardHeader>
              <CardTitle>{t('admin.tabs.sla')}</CardTitle>
            </CardHeader>
            <CardBody>
              <SlaTable cards={slaCards} t={t} eurFmt={eurFmt} />
            </CardBody>
          </Card>
        </TabsContent>

        {/* API MATRIX ---------------------------------------------- */}
        <TabsContent value="apiMatrix">
          <Card>
            <CardBody className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">{t('admin.apiMatrix.header.endpoint')}</th>
                    <th className="px-4 py-3">{t('admin.apiMatrix.header.method')}</th>
                    <th className="px-4 py-3 text-end">{t('admin.apiMatrix.header.p95')}</th>
                    <th className="px-4 py-3 text-end">{t('admin.apiMatrix.header.sla')}</th>
                    <th className="px-4 py-3 text-end">{t('admin.apiMatrix.header.successRate')}</th>
                    <th className="px-4 py-3">{t('admin.apiMatrix.header.version')}</th>
                    <th className="px-4 py-3">{t('admin.apiMatrix.header.status')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {endpoints.map((ep) => (
                    <tr key={ep.capability}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{ep.label}</div>
                        <div className="text-xs font-mono text-slate-500">{ep.path}</div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{ep.method}</td>
                      <td className="px-4 py-3 text-end font-mono">
                        {numberFmt.format(ep.p95Ms)} ms
                      </td>
                      <td className="px-4 py-3 text-end font-mono text-slate-500">
                        ≤ {numberFmt.format(ep.slaP95Ms)} ms
                      </td>
                      <td className="px-4 py-3 text-end font-mono">
                        {pctFmt.format(ep.successRate)}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">{ep.partnerVersion}</td>
                      <td className="px-4 py-3">
                        <StatusBadge status={ep.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardBody>
          </Card>
        </TabsContent>

        {/* WEBHOOKS ------------------------------------------------ */}
        <TabsContent value="webhooks">
          <Card>
            <CardHeader className="flex flex-wrap items-center gap-3">
              <CardTitle>{t('admin.tabs.webhooks')}</CardTitle>
              <div className="ms-auto relative w-full max-w-xs">
                <Search className="pointer-events-none absolute start-2 top-2.5 h-4 w-4 text-slate-400" />
                <input
                  className="w-full rounded-md border border-slate-300 bg-white py-2 ps-8 pe-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="topic, target, transactionId…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </CardHeader>
            <CardBody className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">{t('admin.webhooks.header.received')}</th>
                    <th className="px-4 py-3">{t('admin.webhooks.header.topic')}</th>
                    <th className="px-4 py-3">{t('admin.webhooks.header.delivery')}</th>
                    <th className="px-4 py-3">{t('admin.webhooks.header.target')}</th>
                    <th className="px-4 py-3 text-end">{t('admin.webhooks.header.attempts')}</th>
                    <th className="px-4 py-3 text-end">{t('admin.webhooks.header.duration')}</th>
                    <th className="px-4 py-3">{t('admin.webhooks.header.error')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredWebhooks.map((w) => (
                    <tr key={w.webhookId}>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">
                        {dateFmt.format(new Date(w.receivedAt))}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{w.topic}</td>
                      <td className="px-4 py-3">
                        <DeliveryBadge delivery={w.delivery} t={t} />
                      </td>
                      <td className="px-4 py-3 font-mono text-[11px] text-slate-500">{w.target}</td>
                      <td className="px-4 py-3 text-end font-mono">{w.attempts}</td>
                      <td className="px-4 py-3 text-end font-mono">
                        {numberFmt.format(w.durationMs)} ms
                      </td>
                      <td className="px-4 py-3 text-xs text-rose-700">
                        {w.errorCode ? (
                          <span className="inline-flex items-center gap-1">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            {w.errorCode}
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredWebhooks.length === 0 ? (
                <EmptyState
                  icon={<Webhook className="h-6 w-6" />}
                  title="No matching webhooks"
                  description="Try a different search term."
                />
              ) : null}
            </CardBody>
          </Card>
        </TabsContent>

        {/* DLQ ----------------------------------------------------- */}
        <TabsContent value="dlq">
          <Card>
            <CardHeader>
              <CardTitle>{t('admin.dlq.title')}</CardTitle>
              <p className="mt-1 text-sm text-slate-600">{t('admin.dlq.subtitle')}</p>
            </CardHeader>
            <CardBody className="p-0">
              {dlq.length === 0 ? (
                <EmptyState
                  icon={<ShieldCheck className="h-6 w-6 text-emerald-600" />}
                  title="Empty queue"
                  description="All TickStar webhooks have been delivered or acknowledged."
                />
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3">{t('admin.dlq.header.id')}</th>
                      <th className="px-4 py-3">{t('admin.dlq.header.enqueued')}</th>
                      <th className="px-4 py-3">{t('admin.dlq.header.topic')}</th>
                      <th className="px-4 py-3">{t('admin.dlq.header.reason')}</th>
                      <th className="px-4 py-3">{t('admin.dlq.header.tenant')}</th>
                      <th className="px-4 py-3 text-end">{t('admin.dlq.header.attempts')}</th>
                      <th className="px-4 py-3">{t('admin.dlq.header.owner')}</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {dlq.map((row) => (
                      <tr key={row.dlqId}>
                        <td className="px-4 py-3 font-mono text-xs">{row.dlqId}</td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-500">
                          {dateFmt.format(new Date(row.enqueuedAt))}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs">{row.topic}</td>
                        <td className="px-4 py-3 text-slate-700">{row.reason}</td>
                        <td className="px-4 py-3 font-mono text-xs">{row.tenantId}</td>
                        <td className="px-4 py-3 text-end font-mono">{row.retriedAttempts}</td>
                        <td className="px-4 py-3 text-xs text-slate-600">{row.owner}</td>
                        <td className="px-4 py-3 text-end">
                          <Button
                            variant="ghost"
                            disabled={!can('tickstar.requeue')}
                            onClick={async () => {
                              try {
                                await replayWebhook.mutateAsync(row.dlqId);
                              } catch {
                                /* row id is mock-only; the backend replay
                                   may 404 — fall through and remove locally. */
                              }
                              setDlq((prev) => prev.filter((r) => r.dlqId !== row.dlqId));
                              toast({
                                title: t('admin.dlq.actions.replay'),
                                description: `${row.dlqId} replayed.`,
                              });
                            }}
                          >
                            {t('admin.dlq.actions.replay')}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardBody>
          </Card>
        </TabsContent>

        {/* SLA ----------------------------------------------------- */}
        <TabsContent value="sla">
          <Card>
            <CardBody>
              <SlaTable cards={slaCards} t={t} eurFmt={eurFmt} />
            </CardBody>
            <CardBody className="border-t border-slate-100 text-xs text-slate-500">
              <ExternalLink className="me-1 inline h-3 w-3" />
              Source: TickStar SaaS Agreement Appendix B (99.5% availability, service credit table 0–50%).
            </CardBody>
          </Card>
        </TabsContent>

        {/* USAGE --------------------------------------------------- */}
        <TabsContent value="usage">
          <Card>
            <CardHeader>
              <CardTitle>{t('admin.usage.title')}</CardTitle>
              <p className="mt-1 text-sm text-slate-600">{t('admin.usage.subtitle')}</p>
            </CardHeader>
            <CardBody className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <UsageCard
                label={t('admin.usage.transactions')}
                used={usage.monthlyTransactionsUsed}
                cap={usage.monthlyTransactionCap}
                fmt={numberFmt}
              />
              <UsageCard
                label={t('admin.usage.participants')}
                used={usage.participantsRegistered}
                cap={usage.participantCap}
                fmt={numberFmt}
              />
              <UsageCard
                label={t('admin.usage.lookups')}
                used={usage.lookupsUsedThisMonth}
                cap={usage.lookupCap}
                fmt={numberFmt}
              />
              <OverviewCard
                icon={<Activity className="h-5 w-5 text-amber-600" />}
                label={t('admin.usage.daysUntilReset')}
                value={`${usage.daysUntilReset} d`}
              />
              <OverviewCard
                icon={<Cloud className="h-5 w-5 text-blue-600" />}
                label={t('admin.usage.costMtd')}
                value={eurFmt.format(usage.costEurMtd)}
              />
              {usage.overage ? (
                <OverviewCard
                  icon={<AlertTriangle className="h-5 w-5 text-rose-600" />}
                  label={t('admin.usage.overageLabel')}
                  value="Yes"
                  tone="warning"
                />
              ) : null}
            </CardBody>
          </Card>
        </TabsContent>

        {/* DEMO ---------------------------------------------------- */}
        <TabsContent value="demo">
          <LiveDemoPanel t={t} />
        </TabsContent>

        {/* READINESS ----------------------------------------------- */}
        <TabsContent value="readiness">
          <ReadinessPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// --- Live demo panel ---------------------------------------------

function LiveDemoPanel({ t }: { t: (k: string, vars?: Record<string, unknown>) => string }) {
  const { t: tP1 } = useTranslation('p1');
  const gateway = usePeppolGateway();
  const { toast } = useToast();
  const [payload, setPayload] = useState(SAMPLE_SBDH);
  const [sending, setSending] = useState(false);
  const [transactionId, setTransactionId] = useState<string | undefined>(undefined);
  const { transaction, error } = useTransaction(transactionId);
  const [recent, setRecent] = useState<Transaction[]>([]);

  useEffect(() => {
    let cancelled = false;
    void gateway.transactions
      .listTransactions({ includeAcknowledged: true, limit: 10 })
      .then((res) => {
        if (!cancelled) setRecent(res.items);
      });
    return () => {
      cancelled = true;
    };
  }, [gateway, transaction]);

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await gateway.transactions.sendDocument({ payload, networkType: 'PEPPOL' });
      setTransactionId(res.transactionId);
      toast({
        title: 'Accepted by TickStar',
        description: `${res.transactionId} · ${res.messageIdentifier}`,
        tone: 'success',
      });
    } catch (e) {
      const msg = e instanceof PeppolProviderError ? `${e.code} · ${e.message}` : String(e);
      toast({ title: 'Send failed', description: msg, tone: 'danger' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Send via TickStar Transaction API</CardTitle>
          <p className="mt-1 text-xs text-slate-500">
            Submits the SBDH + UBL XML to <code>POST /transactions?networkType=PEPPOL</code>.
            In mock mode the transaction transitions to COMPLETED after ~1.2 s.
          </p>
        </CardHeader>
        <CardBody className="space-y-3">
          <textarea
            value={payload}
            onChange={(e) => setPayload(e.target.value)}
            spellCheck={false}
            rows={14}
            className="w-full rounded-md border border-slate-300 bg-slate-50 p-3 font-mono text-[11px] leading-snug focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <div className="flex flex-wrap gap-2">
            <Button onClick={handleSend} disabled={sending}>
              {sending ? 'Sending…' : 'Send invoice'}
            </Button>
            <Button variant="ghost" onClick={() => setPayload(SAMPLE_SBDH)}>
              Reset payload
            </Button>
            <Button
              variant="ghost"
              onClick={() => setPayload(SAMPLE_SBDH.replace('Invoice-2026-0001', `Invoice-${Date.now()}`))}
            >
              Mutate invoice number
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{tP1('tickstar.transactionStatus')}</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3 text-sm">
          {!transactionId ? (
            <EmptyState
              icon={<Webhook className="h-6 w-6" />}
              title="No transaction yet"
              description="Send the sample payload to see the state machine in action."
            />
          ) : !transaction ? (
            <p className="text-slate-500">Loading…</p>
          ) : (
            <div className="space-y-2 font-mono text-xs">
              <KV k="transactionId" v={transaction.transactionId} />
              <KV k="messageIdentifier" v={transaction.messageIdentifier} />
              <KV k="state" v={t(`states.${transaction.state}`, { defaultValue: transaction.state })} />
              {transaction.status ? <KV k="status" v={transaction.status} /> : null}
              <KV k="direction" v={transaction.direction} />
              <KV k="documentType" v={transaction.documentType} />
              <KV k="createdAt" v={transaction.createdAt} />
              {transaction.completedAt ? <KV k="completedAt" v={transaction.completedAt} /> : null}
              {transaction.errorMessage ? (
                <p className="text-rose-700">
                  <AlertTriangle className="me-1 inline h-3.5 w-3.5" /> {transaction.errorCode} ·{' '}
                  {transaction.errorMessage}
                </p>
              ) : null}
              <details className="rounded-md border border-slate-200 bg-slate-50 p-2">
                <summary className="cursor-pointer text-slate-600">Artefacts ({transaction.artefacts.length})</summary>
                <ul className="mt-2 space-y-1">
                  {transaction.artefacts.map((a) => (
                    <li key={a.artefactId}>
                      <span className="text-slate-500">{a.type}</span>: {a.artefactId} ({a.sizeBytes} B,
                      {a.contentType})
                    </li>
                  ))}
                </ul>
              </details>
              {transaction.state === 'COMPLETED' ? (
                <Button
                  variant="ghost"
                  onClick={async () => {
                    try {
                      await gateway.transactions.acknowledgeTransaction(transaction.transactionId);
                      toast({ title: 'Acknowledged', tone: 'success' });
                      setTransactionId(transaction.transactionId);
                    } catch (e) {
                      const msg =
                        e instanceof PeppolProviderError ? `${e.code} · ${e.message}` : String(e);
                      toast({ title: 'Ack failed', description: msg, tone: 'danger' });
                    }
                  }}
                >
                  Acknowledge
                </Button>
              ) : null}
            </div>
          )}
          {error ? (
            <p className="text-sm text-rose-700">
              <AlertTriangle className="me-1 inline h-3.5 w-3.5" /> {error.message}
            </p>
          ) : null}
        </CardBody>
      </Card>

      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle>Recent transactions (mock store)</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">transactionId</th>
                <th className="px-4 py-3">direction</th>
                <th className="px-4 py-3">state</th>
                <th className="px-4 py-3">status</th>
                <th className="px-4 py-3">sender</th>
                <th className="px-4 py-3">receiver</th>
                <th className="px-4 py-3">createdAt</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {recent.map((tx) => (
                <tr key={tx.transactionId}>
                  <td className="px-4 py-3 font-mono text-xs">{tx.transactionId}</td>
                  <td className="px-4 py-3 font-mono text-xs">{tx.direction}</td>
                  <td className="px-4 py-3">
                    <Badge tone={stateTone(tx.state)}>{tx.state}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    {tx.status ? <Badge tone={tx.status === 'SUCCESSFUL' ? 'success' : 'danger'}>{tx.status}</Badge> : '—'}
                  </td>
                  <td className="px-4 py-3 font-mono text-[11px]">{tx.senderParticipantId}</td>
                  <td className="px-4 py-3 font-mono text-[11px]">{tx.receiverParticipantId}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{tx.createdAt}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>
    </div>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <span className="text-slate-500">{k}:</span> {v}
    </div>
  );
}

function stateTone(state: Transaction['state']): 'success' | 'warning' | 'info' | 'danger' {
  switch (state) {
    case 'COMPLETED':
      return 'success';
    case 'ACKNOWLEDGED':
      return 'info';
    case 'PROCESSING':
    case 'QUARANTINED':
      return 'warning';
    case 'MANUAL_ERROR':
      return 'danger';
    default:
      return 'info';
  }
}

// --- Readiness panel ----------------------------------------------

interface ReadinessItem {
  id: string;
  category: 'credentials' | 'accreditation' | 'backend' | 'observability' | 'security' | 'dr';
  label: string;
  detail?: string;
  status: 'done' | 'in_progress' | 'todo' | 'blocked';
  owner: string;
  dueDate?: string;
}

const READINESS_ITEMS: ReadinessItem[] = [
  // Credentials
  { id: 'cr-1', category: 'credentials', label: 'Sign TickStar SaaS Agreement (White-label · Starter)', status: 'todo', owner: 'ZIP2000 finance', dueDate: '2026-06-15' },
  { id: 'cr-2', category: 'credentials', label: 'Receive trial credentials (client_id, secret, SMP token)', detail: 'From Perry Liolios', status: 'todo', owner: 'Suti Péter', dueDate: '2026-06-15' },
  { id: 'cr-3', category: 'credentials', label: 'Load secrets into AWS Secrets Manager', detail: 'TICKSTAR_CLIENT_SECRET, TICKSTAR_SMP_TOKEN, TICKSTAR_WEBHOOK_SECRET', status: 'todo', owner: 'Galambos László', dueDate: '2026-06-30' },
  { id: 'cr-4', category: 'credentials', label: 'Quarterly rotation runbook documented', status: 'todo', owner: 'Suti Péter' },

  // Accreditation (15-step)
  { id: 'ac-1', category: 'accreditation', label: 'Step 1 — OpenPeppol membership (AP 2b)', status: 'todo', owner: 'Suti Péter', dueDate: '2026-06-01' },
  { id: 'ac-2', category: 'accreditation', label: 'Step 2 — White-label customer agreement countersigned', status: 'todo', owner: 'Suti Péter', dueDate: '2026-06-15' },
  { id: 'ac-3', category: 'accreditation', label: 'Steps 4–8 — Test WL AP provisioned with Solara PKI', status: 'todo', owner: 'TickStar', dueDate: '2026-07-31' },
  { id: 'ac-4', category: 'accreditation', label: 'Step 9 — UAE sandbox end-to-end test', status: 'todo', owner: 'Galambos László', dueDate: '2026-08-31' },
  { id: 'ac-5', category: 'accreditation', label: 'Steps 10–11 — FTA accreditation + PSPA signed', status: 'todo', owner: 'Suti Péter', dueDate: '2026-09-15' },
  { id: 'ac-6', category: 'accreditation', label: 'Steps 13–15 — Production WL AP handover', status: 'todo', owner: 'TickStar', dueDate: '2026-10-15' },

  // Backend
  { id: 'be-1', category: 'backend', label: 'M4 PeppolModule — LiveTickstarTransactionProvider wired', status: 'todo', owner: 'Galambos László', dueDate: '2026-08-15' },
  { id: 'be-2', category: 'backend', label: 'Webhook receiver POST /webhooks/tickstar (HMAC + idempotency)', status: 'todo', owner: 'Galambos László', dueDate: '2026-08-31' },
  { id: 'be-3', category: 'backend', label: 'Kafka outbound pipeline (intake → SBDH → validate → send → archive → ack)', status: 'todo', owner: 'Galambos László', dueDate: '2026-09-15' },
  { id: 'be-4', category: 'backend', label: 'Customer onboarding wired to live SMP Manager', status: 'todo', owner: 'Galambos László', dueDate: '2026-09-30' },

  // Observability
  { id: 'ob-1', category: 'observability', label: 'Structured pino logs with trace-id', status: 'todo', owner: 'Galambos László' },
  { id: 'ob-2', category: 'observability', label: 'Prometheus metrics (tickstar_*)', status: 'todo', owner: 'Galambos László' },
  { id: 'ob-3', category: 'observability', label: 'Grafana dashboard "Solara · TickStar"', status: 'todo', owner: 'Galambos László' },
  { id: 'ob-4', category: 'observability', label: 'Sentry error reporting on non-retryable PeppolProviderError', status: 'todo', owner: 'Galambos László' },

  // Security
  { id: 'se-1', category: 'security', label: 'mTLS PKI rotation runbook', status: 'todo', owner: 'Suti Péter' },
  { id: 'se-2', category: 'security', label: 'Webhook signing secret rotation procedure', status: 'todo', owner: 'Galambos László' },
  { id: 'se-3', category: 'security', label: 'OWASP review of inbound /webhooks/tickstar endpoint', status: 'todo', owner: 'Galambos László' },
  { id: 'se-4', category: 'security', label: 'External pentest covers Peppol path', status: 'todo', owner: 'Suti Péter' },

  // DR
  { id: 'dr-1', category: 'dr', label: 'TickStar EU outage failure mode documented', status: 'todo', owner: 'Galambos László' },
  { id: 'dr-2', category: 'dr', label: 'Webhook DLQ replay tested', status: 'in_progress', owner: 'Galambos László', detail: 'DLQ tab demo functional, real replay path TBD' },
  { id: 'dr-3', category: 'dr', label: 'DR drill 30 days before go-live', status: 'todo', owner: 'Suti Péter', dueDate: '2026-09-30' },
];

const CATEGORY_LABEL: Record<ReadinessItem['category'], string> = {
  credentials: 'Credentials & secrets',
  accreditation: 'Accreditation (15-step)',
  backend: 'Backend wiring',
  observability: 'Observability',
  security: 'Security',
  dr: 'Disaster recovery',
};

const STATUS_TONE: Record<ReadinessItem['status'], 'success' | 'info' | 'warning' | 'danger' | 'neutral'> = {
  done: 'success',
  in_progress: 'info',
  todo: 'neutral',
  blocked: 'danger',
};

function ReadinessPanel() {
  const grouped = useMemo(() => {
    const acc: Record<string, ReadinessItem[]> = {};
    for (const it of READINESS_ITEMS) {
      (acc[it.category] ??= []).push(it);
    }
    return acc;
  }, []);

  const total = READINESS_ITEMS.length;
  const done = READINESS_ITEMS.filter((i) => i.status === 'done').length;
  const inProgress = READINESS_ITEMS.filter((i) => i.status === 'in_progress').length;
  const blocked = READINESS_ITEMS.filter((i) => i.status === 'blocked').length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <OverviewCard icon={<CheckCircle2 />} label="Done" value={`${done} / ${total}`} />
        <OverviewCard icon={<Activity />} label="In progress" value={String(inProgress)} />
        <OverviewCard icon={<AlertTriangle />} label="Blocked" value={String(blocked)} tone={blocked > 0 ? 'warning' : 'default'} />
        <OverviewCard icon={<Gauge />} label="Pilot go-live" value="2026-10-30" />
      </div>

      {Object.entries(grouped).map(([cat, items]) => (
        <Card key={cat}>
          <CardHeader>
            <CardTitle>{CATEGORY_LABEL[cat as ReadinessItem['category']]}</CardTitle>
          </CardHeader>
          <CardBody className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3 w-1/2">Item</th>
                  <th className="px-4 py-3">Owner</th>
                  <th className="px-4 py-3">Due</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((it) => (
                  <tr key={it.id}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{it.label}</div>
                      {it.detail ? (
                        <div className="text-xs text-slate-500">{it.detail}</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-700">{it.owner}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{it.dueDate ?? '—'}</td>
                    <td className="px-4 py-3">
                      <Badge tone={STATUS_TONE[it.status]}>{it.status.replace('_', ' ')}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      ))}
    </div>
  );
}

// --- Sample SBDH payload (live demo) ------------------------------

const SAMPLE_SBDH = `<?xml version="1.0" encoding="UTF-8"?>
<StandardBusinessDocument xmlns="http://www.unece.org/cefact/namespaces/StandardBusinessDocumentHeader">
  <StandardBusinessDocumentHeader>
    <HeaderVersion>1.0</HeaderVersion>
    <Sender>
      <Identifier Authority="iso6523-actorid-upis">0230:UAE-TRN-100000000000003</Identifier>
    </Sender>
    <Receiver>
      <Identifier Authority="iso6523-actorid-upis">0230:UAE-TRN-200000000000017</Identifier>
    </Receiver>
    <DocumentIdentification>
      <Standard>urn:oasis:names:specification:ubl:schema:xsd:Invoice-2</Standard>
      <TypeVersion>2.1</TypeVersion>
      <InstanceIdentifier>00000000-0000-4000-8000-000000000001</InstanceIdentifier>
      <Type>Invoice</Type>
      <CreationDateAndTime>2026-05-14T10:00:00Z</CreationDateAndTime>
    </DocumentIdentification>
  </StandardBusinessDocumentHeader>
  <Invoice xmlns="urn:oasis:names:specification:ubl:schema:xsd:Invoice-2"
           xmlns:cbc="urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2">
    <cbc:ID>Invoice-2026-0001</cbc:ID>
    <cbc:IssueDate>2026-05-14</cbc:IssueDate>
    <cbc:DocumentCurrencyCode>AED</cbc:DocumentCurrencyCode>
    <cbc:Note>DEMO invoice generated by the Solara · TickStar Live demo tab.</cbc:Note>
  </Invoice>
</StandardBusinessDocument>`;

// --- Small helpers ------------------------------------------------

function OverviewCard({
  icon,
  label,
  value,
  tone = 'default',
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone?: 'default' | 'warning';
}) {
  return (
    <div
      className={cn(
        'rounded-xl border bg-white p-4 shadow-sm',
        tone === 'warning' ? 'border-rose-200 bg-rose-50' : 'border-slate-200',
      )}
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500">
        {icon}
        {label}
      </div>
      <div className="mt-2 text-xl font-semibold text-slate-900">{value}</div>
    </div>
  );
}

function UsageCard({
  label,
  used,
  cap,
  fmt,
}: {
  label: string;
  used: number;
  cap: number;
  fmt: Intl.NumberFormat;
}) {
  const ratio = Math.min(1, used / cap);
  const tone = ratio > 0.9 ? 'bg-rose-500' : ratio > 0.7 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-semibold text-slate-900">
        {fmt.format(used)}{' '}
        <span className="text-sm text-slate-500">/ {fmt.format(cap)}</span>
      </div>
      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-100">
        <div className={cn('h-full', tone)} style={{ width: `${ratio * 100}%` }} />
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: TickstarApiEndpoint['status'] }) {
  if (status === 'healthy')
    return <Badge tone="success">healthy</Badge>;
  if (status === 'degraded')
    return <Badge tone="warning">degraded</Badge>;
  return <Badge tone="danger">failing</Badge>;
}

function DeliveryBadge({
  delivery,
  t,
}: {
  delivery: TickstarWebhookEvent['delivery'];
  t: (key: string) => string;
}) {
  const label = t(`admin.webhooks.delivery.${delivery}`);
  if (delivery === 'delivered') return <Badge tone="success">{label}</Badge>;
  if (delivery === 'retrying') return <Badge tone="warning">{label}</Badge>;
  if (delivery === 'replayed') return <Badge tone="info">{label}</Badge>;
  return <Badge tone="danger">{label}</Badge>;
}

function TrendArrow({ trend }: { trend: TickstarSlaCard['trend'] }) {
  if (trend === 'up') return <ArrowUpRight className="h-4 w-4 text-emerald-600" />;
  if (trend === 'down') return <ArrowDownRight className="h-4 w-4 text-rose-600" />;
  return <Minus className="h-4 w-4 text-slate-400" />;
}

function SlaTable({
  cards,
  t,
  eurFmt,
}: {
  cards: TickstarSlaCard[];
  t: (k: string) => string;
  eurFmt: Intl.NumberFormat;
}) {
  return (
    <table className="w-full text-sm">
      <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
        <tr>
          <th className="py-2">{t('admin.sla.header.bucket')}</th>
          <th className="py-2">{t('admin.sla.header.target')}</th>
          <th className="py-2">{t('admin.sla.header.measured')}</th>
          <th className="py-2 text-end">{t('admin.sla.header.credits')}</th>
          <th className="py-2 text-end">{t('admin.sla.header.trend')}</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {cards.map((c) => (
          <tr key={c.bucket}>
            <td className="py-3 font-medium text-slate-800">
              {t(`admin.sla.bucket.${c.bucket}`)}
            </td>
            <td className="py-3 font-mono text-xs text-slate-500">{c.target}</td>
            <td className="py-3 font-mono">{c.measured}</td>
            <td className="py-3 text-end font-mono">
              {c.creditsEur > 0 ? eurFmt.format(c.creditsEur) : '—'}
            </td>
            <td className="py-3 text-end">
              <TrendArrow trend={c.trend} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
